/**
 * Signing out of Olive. The platform's own sign-out address
 * (/.auth/logout) walks the browser through Microsoft's end-session
 * page, which asks "which account do you want to sign out of?" whenever
 * more than one Microsoft account is open in the browser, and clears
 * Olive's session only once that page has been answered. Left
 * unanswered, the person is still signed in.
 *
 * So Olive ends its own session directly: the two platform addresses
 * are called in the background, which clears the session cookie without
 * the browser leaving the page, and the person then lands on the Peace
 * sign-in screen. Their Microsoft session is left alone, as when any one
 * Microsoft 365 app is closed. If the background calls cannot be made
 * (offline, blocked), the platform address is used instead, so a
 * failure never leaves someone signed in.
 *
 * Nothing of a client's stays behind: the page is hidden before anything
 * else happens, so any picture the browser keeps of it is blank; session
 * storage and every local key but the kept preferences, the note drafts
 * and the cross-tab signal are cleared before the session is ended; and
 * the signal key goes last, once the session has ended, so the other open
 * tabs that reload on it find nobody signed in. On the fallback the key
 * stays: the platform page in the middle ends the session, and the next
 * boot that finds nobody signed in removes it.
 */

import { hideDocument } from '@/lib/pageRestoreGuard';
import { clearSignedInStorage, forgetSignedInUser } from '@/lib/signedInStorage';

/** Why the person is being signed out: the idle clock ran out, or they
 *  chose Sign out. The sign-in screen says so for the idle case. */
export type SignOutReason = 'idle' | 'chosen';

/** True from the moment a sign-out starts until the page is gone. The
 *  reads that fail while the session is being cleared (a focus refresh,
 *  the bell's poll) must not answer with their own trip to Microsoft's
 *  sign-in: the sign-in screen is already on its way (src/lib/api.ts
 *  authExpiry). */
let inProgress = false;

export function signOutInProgress(): boolean {
  return inProgress;
}

/** Tests only: a page is gone after a real sign-out; a test is not. */
export function resetSignOutStateForTests(): void {
  inProgress = false;
}

/** Where the person lands once the session is gone. */
export function signedOutLanding(reason: SignOutReason): string {
  return reason === 'idle' ? '/?signed_out=idle' : '/';
}

/** The platform's sign-out address (the Microsoft page in the middle):
 *  the fallback when the background calls fail. */
export function platformSignOutUrl(reason: SignOutReason): string {
  return `/.auth/logout?post_logout_redirect_uri=${encodeURIComponent(signedOutLanding(reason))}`;
}

const PLATFORM_SIGN_OUT_STEPS = ['/.auth/logout', '/.auth/logout/complete'] as const;

/** Clears Olive's session cookie in the background. Both addresses must
 *  be called in this order: the first starts the platform's sign-out,
 *  the second completes it. `no-cors` because the first one redirects
 *  through Microsoft; the responses are never read. Resolves false when
 *  a call could not be made. */
export async function endOliveSession(fetchFn: typeof fetch = fetch): Promise<boolean> {
  try {
    for (const step of PLATFORM_SIGN_OUT_STEPS) {
      await fetchFn(step, { mode: 'no-cors', credentials: 'include', cache: 'no-store' });
    }
    return true;
  } catch {
    return false;
  }
}

export interface SignOutDeps {
  fetchFn?: typeof fetch;
  navigate?: (url: string) => void;
}

/** End the session and land on the sign-in screen. */
export async function signOut(reason: SignOutReason, deps: SignOutDeps = {}): Promise<void> {
  hideDocument();
  inProgress = true;
  const fetchFn = deps.fetchFn ?? ((input, init) => fetch(input, init));
  const navigate = deps.navigate ?? ((url: string) => window.location.assign(url));
  clearSignedInStorage();
  const ended = await endOliveSession(fetchFn);
  if (ended) {
    forgetSignedInUser();
    navigate(signedOutLanding(reason));
  } else {
    navigate(platformSignOutUrl(reason));
  }
}
